import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { v4 } from 'uuid';

import {
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  IMAGE,
  LOCATION,
  MAINTENANCE,
  MAP,
  TEXT_STATIC,
  TEXT_TAGS,
} from '../../Constants/WidgetTypes';
import GatewayReminderInstanceService from '../../Services/GatewayReminderInstanceService';
import TagService from '../../Services/TagService';
import TemplateDashboardService from '../../Services/TemplateDashboardService';
import TemplateService from '../../Services/TemplateService';
import WidgetUpdateHelper from '../../Lib/WidgetUpdateHelper';
import WidgetUtils from '../../Lib/WidgetUtils';

import EditWidgetModalDisplay from './EditWidgetModalDisplay';

export default memo(EditWidgetModal);

const initState = {
  activeImageCoordinate: new Map(),
  displayTextAsGrid: false,
  editRequestPending: false,
  image: '',
  imageCoordinatesMap: new Map(),
  maintenanceLoading: true,
  reminderInstanceOptions: [],
  selectedAggregation: '',
  selectedInstances: [],
  showTimeSinceLastUpdated: false,
  tagsLoading: true,
  widgetName: '',
  widgetSignals: [],
  widgetType: {},
  yMax: null,
  yMin: null,
};

function EditWidgetModal(props) {
  const source = CancelToken.source();
  const dashboardWidgetService = useMemo(
      () => new TemplateDashboardService(),
      []
    ),
    reminderInstanceService = useMemo(
      () => new GatewayReminderInstanceService(),
      []
    ),
    tagService = useMemo(() => new TagService(), []),
    templateService = useMemo(() => new TemplateService(), []),
    widgetUpdateHelper = useMemo(() => new WidgetUpdateHelper(), []),
    widgetUtils = useMemo(() => new WidgetUtils(), []),
    widgetTypes = useMemo(
      () => [...widgetUtils.types, ...widgetUtils.legacyTypes],
      [widgetUtils.legacyTypes, widgetUtils.types]
    );

  const imageOverlayRef = useRef();

  const [state, updateState] = useState(() => {
    const {
      aggregation: selectedAggregation,
      displayTextAsGrid = false,
      image = '',
      showTimeSinceLastUpdated = false,
      title: widgetName = '',
      yMax = null,
      yMin = null,
    } = props.widget?.settings || {};

    const datapointsMap = widgetUtils.createDatapointMapForDisplay(
      props.asset?.gtemplate?.datapoints
    );

    const widgetType =
      widgetTypes.find(({ value } = {}) => value === props.widget?.type) || {};

    return {
      ...initState,
      datapointsMap,
      displayTextAsGrid,
      image,
      selectedAggregation,
      showTimeSinceLastUpdated,
      widgetName,
      widgetType,
      yMax,
      yMin,
    };
  });

  const editWidget = async () => {
    const {
      asset: { gtemplateID, gtemplate },
      dashboardID,
      handleWidgetUpdated,
      toggleModal,
      widget,
      widget: { _id: widgetID },
    } = props;
    try {
      updateState((prevState) => ({ ...prevState, editRequestPending: true }));

      const body = widgetUpdateHelper.buildUpdateRequestBody(
        state,
        widget,
        gtemplate,
        state.imageCoordinatesMap
      );
      const { widgets } =
        await dashboardWidgetService.updateTemplateDashboardWidget(
          gtemplateID,
          dashboardID,
          widgetID,
          body,
          source.token
        );

      handleWidgetUpdated(widgets);
      toggleModal();
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        editRequestPending: false,
        error,
      }));
    }
  };

  const handleAddImageOverlaySignal = useCallback((coordinates, signal) => {
    updateState((prevState) => {
      const imageCoordinatesMap = new Map(prevState.imageCoordinatesMap).set(
        coordinates,
        signal
      );
      return {
        ...prevState,
        imageCoordinatesMap,
        activeImageCoordinate: new Map(),
      };
    });
  }, []);

  const handleAddRange = (outerIndex, innerIndex) => {
    if (innerIndex >= 4) return;
    try {
      const widgetSignals = state.widgetSignals.map((s, index) => {
        if (index !== outerIndex) return s;
        const validRange = s.ranges?.every?.(
          ({ color, label, maxValue, minValue } = {}) => {
            return color && label && maxValue && minValue;
          }
        );
        if (!validRange) {
          throw new Error(
            'Each range must have a Min/Max Value, Label and Color.'
          );
        }

        const { maxValue } = s.ranges[innerIndex] || {};
        return {
          ...s,
          ranges: s.ranges.concat({ id: v4(), minValue: maxValue }),
        };
      });
      updateState((prevState) => ({ ...prevState, error: '', widgetSignals }));
    } catch ({ message: error }) {
      updateState((prevState) => ({ ...prevState, error }));
    }
  };

  const handleAddStaticTextLink = useCallback(() => {
    updateState((prevState) => {
      const [{ links: prevLinks = [] }] = prevState.widgetSignals;
      const links = [...prevLinks, { display: '', link: '' }];
      return {
        ...prevState,
        widgetSignals: [{ ...prevState.widgetSignals[0], links }],
      };
    });
  }, []);

  const handleAddWidgetCommand = useCallback((command, index) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) =>
        i === index ? command : s
      );
      return { ...prevState, widgetSignals, error: '' };
    });
  }, []);

  const handleAddWidgetSignal = useCallback(() => {
    updateState((prevState) => {
      if (
        prevState.widgetSignals?.length === prevState.widgetType?.maxSignals
      ) {
        return prevState;
      }

      const rowToAdd = widgetUtils.nonSignalTypes.has(prevState.widgetType)
        ? {}
        : widgetUtils.widgetSignalDefault;
      return {
        ...prevState,
        widgetSignals: prevState.widgetSignals.concat(rowToAdd),
      };
    });
  }, [widgetUtils.nonSignalTypes, widgetUtils.widgetSignalDefault]);

  const handleAggregationSelect = useCallback((selected) => {
    updateState((prevState) => ({
      ...prevState,
      selectedAggregation: selected?.value,
    }));
  }, []);

  const handleColorInput = useCallback(
    ({ target: { value } }, outerIndex, innerIndex) => {
      const trimmedInput = value.trim();
      const color = trimmedInput.startsWith('#')
        ? trimmedInput
        : '#'.concat(trimmedInput);
      updateState((prevState) => {
        const widgetSignals = prevState.widgetSignals.map((s, i) => {
          if (i !== outerIndex) return s;
          const ranges = s.ranges.map((r, index) =>
            index === innerIndex ? { ...r, color } : r
          );
          return { ...s, ranges };
        });
        return { ...prevState, widgetSignals };
      });
    },
    []
  );

  const handleColorSelected = useCallback((color, outerIndex, innerIndex) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) => {
        if (i !== outerIndex) return s;
        const ranges = s.ranges.map((r, index) =>
          index === innerIndex ? { ...r, color } : r
        );
        return { ...s, ranges };
      });
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleCoordinateClick = useCallback((x, y, overlayConfig) => {
    updateState((prevState) => {
      const activeImageCoordinate = new Map([
        ['x', Number(x)],
        ['y', Number(y)],
        ['overlayConfig', overlayConfig],
      ]);
      return { ...prevState, activeImageCoordinate };
    });
  }, []);

  const handleDatapointSelect = useCallback((datapoint, index) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) => {
        if (i !== index) return s;
        return { ...s, datapoint, signal: {} };
      });
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleDisplayTextAsGridClick = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      displayTextAsGrid: !prevState.displayTextAsGrid,
    }));
  }, []);

  const handleImageClick = useCallback(({ clientX, clientY, target }) => {
    const { bottom, left, right, top } = target.getBoundingClientRect?.() || {};

    const xCalc = ((clientX - left) * 100) / (right - left);
    const x = Number(xCalc.toFixed());

    const yCalc = ((clientY - top) * 100) / (bottom - top);
    const y = Number(yCalc.toFixed());

    const activeImageCoordinate = new Map([
      ['x', clientX],
      ['y', clientY],
      ['overlayConfig', { x, y }],
    ]);
    updateState((prevState) => ({ ...prevState, activeImageCoordinate }));
  }, []);

  const handleInstanceSelect = useCallback((selectedInstances) => {
    updateState((prevState) => ({ ...prevState, selectedInstances }));
  }, []);

  const handleMinMaxValueUpdate = useCallback(
    ({ target: { name, value } }, index) => {
      updateState((prevState) => {
        const widgetSignals = prevState.widgetSignals.map((s, i) =>
          i === index ? { ...s, [name]: value } : s
        );
        return { ...prevState, widgetSignals };
      });
    },
    []
  );

  const handleNameAndYScaleUpdate = useCallback(
    ({ target: { name, value } }) => {
      updateState((prevState) => ({ ...prevState, [name]: value }));
    },
    []
  );

  const handleOptionDisabled = useCallback(
    (signal) => {
      return state.widgetSignals.some(
        (widgetSignal) => signal.value === widgetSignal.signal.value
      );
    },
    [state.widgetSignals]
  );

  const handleRangeValueUpdate = useCallback(
    ({ target: { name, value } }, outerIndex, innerIndex) => {
      updateState((prevState) => {
        const widgetSignals = prevState.widgetSignals.map((s, index) => {
          if (index !== outerIndex) return s;
          const ranges = s.ranges.map((r, i) => {
            switch (true) {
              case i === innerIndex:
                return { ...r, [name]: value };
              case name === 'maxValue' && innerIndex === i - 1:
                return { ...r, minValue: value };
              default:
                return r;
            }
          });
          return { ...s, ranges };
        });
        return { ...prevState, error: '', widgetSignals };
      });
    },
    []
  );

  const handleRemoveImageOverlaySignal = useCallback((coordinates) => {
    updateState((prevState) => {
      const imageCoordinatesMap = new Map(prevState.imageCoordinatesMap);
      imageCoordinatesMap.delete(coordinates);
      return {
        ...prevState,
        imageCoordinatesMap,
        activeImageCoordinate: new Map(),
      };
    });
  }, []);

  const handleRemoveRange = useCallback((outerIndex, innerIndex) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, index) => {
        if (index !== outerIndex) return s;
        const filtered = s.ranges
          .map((r, i, arr) => {
            if (!innerIndex || i !== innerIndex + 1) return r;
            const { maxValue } = arr[innerIndex - 1] || {};
            return { ...r, minValue: maxValue };
          })
          .filter((r, i) => i !== innerIndex);
        const ranges = filtered.length ? filtered : [{ id: v4() }];
        return { ...s, ranges };
      });
      return { ...prevState, error: '', widgetSignals };
    });
  }, []);

  const handleRemoveStaticTextLink = useCallback((index) => {
    updateState((prevState) => {
      const links = prevState.widgetSignals[0].links.filter(
        (_, i) => i !== index
      );
      return {
        ...prevState,
        widgetSignals: [{ ...prevState.widgetSignals[0], links }],
      };
    });
  }, []);

  const handleRemoveWidgetSignal = useCallback(
    (index) => {
      updateState((prevState) => {
        const update = prevState.widgetSignals.filter((x, i) => i !== index);
        const widgetSignals = update.length
          ? update
          : [
              widgetUtils.nonSignalTypes.has(prevState.widgetType)
                ? {}
                : widgetUtils.widgetSignalDefault,
            ];
        return { ...prevState, widgetSignals };
      });
    },
    [widgetUtils.nonSignalTypes, widgetUtils.widgetSignalDefault]
  );

  const handleSignalSelect = useCallback((signal, index) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) =>
        i === index ? { ...s, signal } : s
      );
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleStaticTextInput = useCallback(({ target: { value } }) => {
    updateState((prevState) => ({
      ...prevState,
      widgetSignals: [{ ...prevState.widgetSignals[0], value }],
      error: '',
    }));
  }, []);

  const handleStaticTextLinkInput = useCallback(
    ({ target: { name, value } }, i) => {
      updateState((prevState) => {
        const links = prevState.widgetSignals[0].links?.map((link, index) =>
          index === i ? { ...link, [name]: value } : link
        );
        return {
          ...prevState,
          widgetSignals: [{ ...prevState.widgetSignals[0], links }],
        };
      });
    },
    []
  );

  const handleTagSelect = useCallback((selectedTag, index) => {
    if (!selectedTag) return;
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) =>
        i === index ? selectedTag : s
      );
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleTimeSinceLastUpdatedClick = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      showTimeSinceLastUpdated: !prevState.showTimeSinceLastUpdated,
    }));
  }, []);

  const getWidgetSignals = (
    { type, settings } = {},
    datapointsMap,
    asset = {}
  ) => {
    switch (true) {
      case type === COMMAND:
        return getCommands(settings, asset);
      case widgetUtils.settingsSignalArrayNameMap.has(type):
        return getSignalsFromWidgetSettings(
          type,
          settings,
          datapointsMap,
          widgetUtils.settingsSignalArrayNameMap,
          props.asset?.gtemplateID,
          templateService,
          source.token
        );
      case type === GAUGE:
        return getGaugeWidgetSignals(settings);
      case type === GAUGE_RANGE:
        return getFlexibleRangeGaugeSignals(settings);
      case [LOCATION, MAP].includes(type):
        return [];
      case type === TEXT_STATIC:
        return [{ links: settings?.links, value: settings?.staticText || '' }];
      case type === TEXT_TAGS:
        return getTextTags(settings);
      default:
        return [];
    }
  };

  const setAsyncState = async () => {
    try {
      const widgetSignals = await getWidgetSignals(
        props.widget,
        state.datapointsMap,
        props.asset
      );

      switch (true) {
        case props.widget?.type === IMAGE: {
          const { left, height, top, width } =
            imageOverlayRef?.current?.getBoundingClientRect?.() || {};

          const imageCoordinatesMap = new Map(
            props.widget?.settings?.lines?.map?.(({ x, y }) => {
              const clientX = left + width * (x / 100);
              const clientY = top + height * (y / 100);
              const config =
                widgetSignals.find(
                  (signal) => signal.x === x && signal.y === y
                ) || {};
              return [`${clientX}-${clientY}`, config];
            }) || []
          );
          return updateState((prevState) => ({
            ...prevState,
            imageCoordinatesMap,
            tagsLoading: false,
            widgetSignals,
          }));
        }
        case props.widget?.type === MAINTENANCE: {
          if (!state.maintenanceLoading) return;
          try {
            const params = {
              gateway_id: props.asset?._id,
              limit: 500,
              page: 1,
            };
            const { data } =
              await reminderInstanceService.getPaginatedGatewayReminderInstances(
                params,
                source.token
              );
            const reminderInstanceOptions = data?.map?.((instance) => ({
                label: instance?.GtemplateReminder?.name,
                value: instance?.GtemplateReminder?._id,
              })),
              selectedInstances = props.widget?.settings?.maintenance?.map?.(
                (instance) => ({
                  label: instance?.reminder_name,
                  value: instance?.reminder_id,
                })
              );
            return updateState((prevState) => ({
              ...prevState,
              maintenanceLoading: false,
              reminderInstanceOptions,
              selectedInstances,
            }));
          } catch (err) {
            if (isCancel(err)) return;
            const error = err.response?.data?.message ?? err.message;
            return updateState((prevState) => ({
              ...prevState,
              error,
              maintenanceLoading: false,
            }));
          }
        }
        case props.widget?.type !== TEXT_TAGS || state.tagsLoaded:
          return updateState((prevState) => ({
            ...prevState,
            tagsLoading: false,
            widgetSignals,
          }));
        default: {
          const params = { orgID: props.asset?.orgID };
          const tags = await tagService.getTags(params, source.token);
          const tagSet = new Set(tags.map(({ name }) => name));
          const tagOptions = Array.from(tagSet).map((t) => ({
            label: t,
            value: t,
          }));
          updateState((prevState) => ({
            ...prevState,
            tagOptions,
            tagsLoaded: true,
            tagsLoading: false,
            widgetSignals,
          }));
        }
      }
    } catch (err) {
      if (isCancel(err)) return;
      const error = err?.response?.data?.message ?? err.message;
      updateState((prevState) => ({ ...prevState, error, tagsLoading: false }));
    }
  };

  const toggleImageOverlayModal = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      activeImageCoordinate: new Map(),
    }));
  }, []);

  useEffect(() => {
    setAsyncState();
    return source.cancel;
  }, []);

  return (
    <EditWidgetModalDisplay
      {...props}
      {...state}
      editWidget={editWidget}
      handleAddImageOverlaySignal={handleAddImageOverlaySignal}
      handleAddRange={handleAddRange}
      handleAddStaticTextLink={handleAddStaticTextLink}
      handleAddWidgetCommand={handleAddWidgetCommand}
      handleAddWidgetSignal={handleAddWidgetSignal}
      handleAggregationSelect={handleAggregationSelect}
      handleColorInput={handleColorInput}
      handleColorSelected={handleColorSelected}
      handleCoordinateClick={handleCoordinateClick}
      handleDatapointSelect={handleDatapointSelect}
      handleDisplayTextAsGridClick={handleDisplayTextAsGridClick}
      handleImageClick={handleImageClick}
      handleInstanceSelect={handleInstanceSelect}
      handleMinMaxValueUpdate={handleMinMaxValueUpdate}
      handleNameAndYScaleUpdate={handleNameAndYScaleUpdate}
      handleOptionDisabled={handleOptionDisabled}
      handleRangeValueUpdate={handleRangeValueUpdate}
      handleRemoveImageOverlaySignal={handleRemoveImageOverlaySignal}
      handleRemoveRange={handleRemoveRange}
      handleRemoveStaticTextLink={handleRemoveStaticTextLink}
      handleRemoveWidgetSignal={handleRemoveWidgetSignal}
      handleSignalSelect={handleSignalSelect}
      handleStaticTextInput={handleStaticTextInput}
      handleStaticTextLinkInput={handleStaticTextLinkInput}
      handleTagSelect={handleTagSelect}
      handleTimeSinceLastUpdatedClick={handleTimeSinceLastUpdatedClick}
      imageOverlayRef={imageOverlayRef}
      timeAggregationOptions={widgetUtils.timeAggregationOptions}
      toggleImageOverlayModal={toggleImageOverlayModal}
      updateState={updateState}
    />
  );
}

EditWidgetModal.propTypes = {
  asset: PropTypes.object,
  dashboardID: PropTypes.string,
  handleWidgetUpdated: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  updateWidgets: PropTypes.func,
  widget: PropTypes.object,
};

function getCommands({ commands = [] }, { gtemplate }) {
  return commands.map?.((c) => {
    const { name: label = '' } =
      gtemplate?.commands?.find?.(({ _id }) => _id === c.commandID) || {};
    return {
      ...c,
      command: { label, value: c.commandID },
    };
  });
}

function getFlexibleRangeGaugeSignals(settings) {
  const {
    datapoint,
    datapoint: { name, values },
    datapointID,
    key,
    ranges,
    secondGauge: {
      datapoint: datapoint2,
      datapoint: { name: name2, values: values2 } = {},
      datapointID: datapointID2,
      key: key2,
      ranges: ranges2,
      type: secondGaugeType,
    } = {},
    type,
  } = settings;
  if (type !== 'outputSignal' && !values?.length) return [];

  let gaugeSignals;
  const firstGaugeRanges = ranges.map((range) => {
    const { color, label, max, min } = range || {};
    const maxValue = String(max);
    const minValue = String(min);
    return { color, id: v4(), label, maxValue, minValue };
  });

  if (type === 'outputSignal') {
    gaugeSignals = [
      {
        datapoint: { label: 'Calculated Signal', value: 'outputSignal' },
        ranges: firstGaugeRanges,
        signal: { label: name, value: datapoint },
      },
    ];
  } else {
    const { _id: signalID } = values.find(({ name, label }) => {
      return name === key || label === key;
    });
    gaugeSignals = [
      {
        datapoint: { label: name, value: datapointID, values },
        ranges: firstGaugeRanges,
        signal: { label: key, value: signalID },
      },
    ];
  }
  if (!datapointID2) return gaugeSignals;

  const secondGaugeRanges = ranges2.map((range) => {
    const { color, label, max, min } = range || {};
    const maxValue = String(max);
    const minValue = String(min);
    return { color, id: v4(), label, maxValue, minValue };
  });
  if (secondGaugeType === 'outputSignal') {
    return [
      ...gaugeSignals,
      {
        datapoint: { label: 'Calculated Signal', value: 'outputSignal' },
        ranges: secondGaugeRanges,
        signal: { label: name2, value: datapoint2 },
      },
    ];
  }

  const { _id: signalID2 } = values2.find(({ label, name }) => {
    return label === key2 || name === key2;
  });

  return [
    ...gaugeSignals,
    {
      datapoint: { label: name2, value: datapointID2, values: values2 },
      ranges: secondGaugeRanges,
      signal: { label: key2, value: signalID2 },
    },
  ];
}

function getGaugeWidgetSignals(settings) {
  const {
    datapoint,
    datapoint: { name, values },
    datapointID,
    key,
    max,
    min,
    secondGauge: {
      datapoint: datapoint2,
      datapoint: { name: name2, values: values2 } = {},
      datapointID: datapointID2,
      key: key2,
      max: max2,
      min: min2,
      type: secondGaugeType,
    } = {},
    type,
  } = settings;
  if (type !== 'outputSignal' && !values?.length) return [];

  const maxValue = String(max);
  const minValue = String(min);
  let gaugeSignals;
  if (type === 'outputSignal') {
    gaugeSignals = [
      {
        datapoint: { label: 'Calculated Signal', value: 'outputSignal' },
        maxValue,
        minValue,
        signal: { label: name, value: datapoint },
      },
    ];
  } else {
    const { _id: signalID } = values.find(({ name, label }) => {
      return name === key || label === key;
    });
    gaugeSignals = [
      {
        datapoint: { label: name, value: datapointID, values },
        maxValue,
        minValue,
        signal: { label: key, value: signalID },
      },
    ];
  }

  if (!datapointID2) return gaugeSignals;

  const maxValue2 = String(max2);
  const minValue2 = String(min2);
  if (secondGaugeType === 'outputSignal') {
    return [
      ...gaugeSignals,
      {
        datapoint: { label: 'Calculated Signal', value: 'outputSignal' },
        maxValue: maxValue2,
        minValue: minValue2,
        signal: { label: name2, value: datapoint2 },
      },
    ];
  }

  const { _id: signalID2 } = values2.find(({ label, name }) => {
    return label === key2 || name === key2;
  });
  return [
    ...gaugeSignals,
    {
      datapoint: { label: name2, value: datapointID2, values: values2 },
      maxValue: maxValue2,
      minValue: minValue2,
      signal: { label: key2, value: signalID2 },
    },
  ];
}

async function getSignalsFromWidgetSettings(
  type,
  settings,
  datapointsMap,
  settingsSignalArrayNameMap,
  templateID,
  templateService,
  cancelToken
) {
  const currentDatapoints = settings[settingsSignalArrayNameMap.get(type)];
  if (!currentDatapoints?.length) return [];

  const { eventDatapoints, outputSignals } = currentDatapoints.reduce(
    (acc, { datapointID, key, type, x, y } = {}) => {
      if (type === 'outputSignal') {
        acc.outputSignals.push({ datapointID, x, y });
      } else {
        const {
          label,
          value,
          values = [],
        } = datapointsMap.find(({ value }) => value === datapointID) || {};
        const { label: signalLabel = '', value: signalValue = '' } =
          values.find(({ label }) => label === key) || {};
        acc.eventDatapoints.push({
          datapoint: { label, value, values },
          signal: { label: signalLabel, value: signalValue },
          x,
          y,
        });
      }
      return acc;
    },
    { eventDatapoints: [], outputSignals: [] }
  );
  if (!outputSignals.length) return eventDatapoints;

  const outputSignalProms = outputSignals.map(({ datapointID }) => {
    return templateService.fetchOutputSignal(
      templateID,
      datapointID,
      cancelToken
    );
  });
  const outputSignalsRes = await Promise.all(outputSignalProms);
  const formattedOutputSignals = outputSignalsRes?.map((os, i) => ({
    datapoint: { label: 'Calculated Signal', value: 'outputSignal' },
    signal: { label: os?.name, value: os },
    x: outputSignals[i].x,
    y: outputSignals[i].y,
  }));
  return eventDatapoints.concat(formattedOutputSignals);
}

function getTextTags({ tags = [] } = {}) {
  return tags.map((t) => ({ label: t, value: t }));
}
